import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ms } from "date-fns/locale";
import { MapContainer, TileLayer, CircleMarker, Polyline, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { ArrowLeft, CalendarDays, Clock, MapPin, Users, Route, Send, Building2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const TIME_OPTIONS = [];
for (let h = 6; h <= 22; h++) {
  for (const m of [0, 30]) {
    if (h === 22 && m === 30) continue;
    const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    const h12 = h % 12 === 0 ? 12 : h % 12;
    TIME_OPTIONS.push({ value, label: `${h12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}` });
  }
}

function DateField({ label, value, onChange, minDate, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" data-testid={testId}
            className="w-full justify-start font-normal h-10">
            <CalendarDays className="h-4 w-4 mr-2 text-[#10056E]" />
            {value ? format(value, "dd MMM yyyy", { locale: ms }) : "Pilih tarikh"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-white" align="start">
          <Calendar mode="single" selected={value} locale={ms}
            disabled={(d) => d < (minDate || new Date(new Date().setHours(0, 0, 0, 0)))}
            onSelect={(d) => { if (d) { onChange(d); setOpen(false); } }} />
        </PopoverContent>
      </Popover>
    </div>
  );
}

function TimeField({ label, value, onChange, testId }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-10" data-testid={testId}>
          <Clock className="h-4 w-4 mr-2 text-[#10056E]" />
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-white max-h-64">
          {TIME_OPTIONS.map((t) => (
            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) map.flyTo(points[0], 12);
    else if (points.length > 1) map.fitBounds(points, { padding: [30, 30] });
  }, [JSON.stringify(points), map]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export default function NewBooking() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canOnBehalf = user?.role === "admin" || user?.role === "operator";
  const [drivers, setDrivers] = useState([]);
  const [bookFor, setBookFor] = useState("self");
  const [locations, setLocations] = useState([]);
  const [originId, setOriginId] = useState("");
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [startTime, setStartTime] = useState("08:00");
  const [endTime, setEndTime] = useState("18:00");
  const [form, setForm] = useState({ destination: "", purpose: "", estimated_distance: "", passengers: 1 });
  const [dest, setDest] = useState(null); // {lat, lng}
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [routeInfo, setRouteInfo] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (canOnBehalf) {
      api.get("/users").then((r) => setDrivers(r.data.filter((u) => u.role === "driver"))).catch(() => {});
    }
    api.get("/locations").then((r) => {
      setLocations(r.data);
      const hq = r.data.find((l) => l.is_hq) || r.data[0];
      if (hq) setOriginId(hq.id);
    }).catch(() => {});
  }, [canOnBehalf]);

  const origin = locations.find((l) => l.id === originId);

  // Destination autocomplete (debounced)
  useEffect(() => {
    if (dest || form.destination.trim().length < 3) { setSuggestions([]); return; }
    setSearching(true);
    const t = setTimeout(() => {
      api.get("/geo/search", { params: { q: form.destination } })
        .then((r) => setSuggestions(r.data))
        .catch(() => setSuggestions([]))
        .finally(() => setSearching(false));
    }, 500);
    return () => clearTimeout(t);
  }, [form.destination, dest]);

  // Auto distance calculation
  useEffect(() => {
    if (!origin || !dest) { setRouteInfo(null); return; }
    api.get("/geo/route", { params: { from_lat: origin.lat, from_lng: origin.lng, to_lat: dest.lat, to_lng: dest.lng } })
      .then((r) => {
        setRouteInfo(r.data);
        setForm((f) => ({ ...f, estimated_distance: r.data.distance_km }));
      }).catch(() => {});
  }, [origin?.id, dest?.lat, dest?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickSuggestion = (s) => {
    const short = s.name.split(",").slice(0, 3).join(",");
    setForm((f) => ({ ...f, destination: short }));
    setDest({ lat: s.lat, lng: s.lng });
    setSuggestions([]);
  };

  const mapPoints = useMemo(() => {
    const pts = [];
    if (origin) pts.push([origin.lat, origin.lng]);
    if (dest) pts.push([dest.lat, dest.lng]);
    return pts;
  }, [origin, dest]);

  const summary = useMemo(() => {
    const s = `${format(startDate, "dd MMM yyyy", { locale: ms })}, ${TIME_OPTIONS.find((t) => t.value === startTime)?.label}`;
    const e = `${format(endDate, "dd MMM yyyy", { locale: ms })}, ${TIME_OPTIONS.find((t) => t.value === endTime)?.label}`;
    return `${s} → ${e}`;
  }, [startDate, endDate, startTime, endTime]);

  const onStartDate = (d) => {
    setStartDate(d);
    if (endDate < d) setEndDate(d);
  };

  const submit = async (e) => {
    e.preventDefault();
    const start_datetime = `${format(startDate, "yyyy-MM-dd")}T${startTime}`;
    const end_datetime = `${format(endDate, "yyyy-MM-dd")}T${endTime}`;
    if (end_datetime <= start_datetime) {
      toast.error("Masa tamat mesti selepas masa mula");
      return;
    }
    setSaving(true);
    try {
      await api.post("/bookings", {
        start_datetime, end_datetime,
        destination: form.destination, purpose: form.purpose,
        estimated_distance: Number(form.estimated_distance),
        passengers: Number(form.passengers || 1),
        ...(form.special_instructions?.trim() ? { special_instructions: form.special_instructions.trim() } : {}),
        ...(origin ? { origin_location_id: origin.id, origin_location_name: origin.name } : {}),
        ...(dest ? { dest_lat: dest.lat, dest_lng: dest.lng } : {}),
        ...(canOnBehalf && bookFor !== "self" ? { driver_id: bookFor } : {}),
      });
      toast.success("Tempahan dihantar untuk kelulusan");
      navigate("/tempahan");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-5 fade-up" data-testid="new-booking-page">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => navigate("/tempahan")} data-testid="new-booking-back-btn">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Tempahan Baharu</h1>
          <p className="text-sm text-muted-foreground">Isi maklumat perjalanan — pengendali akan menugaskan kenderaan yang sesuai.</p>
        </div>
      </div>

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 md:p-6 space-y-5">
        {canOnBehalf && (
          <div className="space-y-1.5">
            <Label>Tempahan Untuk</Label>
            <Select value={bookFor} onValueChange={setBookFor}>
              <SelectTrigger className="h-10" data-testid="book-for-select">
                <Users className="h-4 w-4 mr-2 text-[#10056E]" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-64">
                <SelectItem value="self">Diri Sendiri — {user?.name}</SelectItem>
                {drivers.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name} ({d.email})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <DateField label="Tarikh Mula" value={startDate} onChange={onStartDate} testId="start-date-picker" />
          <TimeField label="Masa Mula" value={startTime} onChange={setStartTime} testId="start-time-select" />
          <DateField label="Tarikh Tamat" value={endDate} onChange={setEndDate} minDate={startDate} testId="end-date-picker" />
          <TimeField label="Masa Tamat" value={endTime} onChange={setEndTime} testId="end-time-select" />
        </div>

        <div className="text-xs bg-indigo-50/70 border border-indigo-100 text-[#10056E] rounded-lg p-3 font-medium" data-testid="booking-period-summary">
          Tempoh: {summary}
        </div>

        {locations.length > 0 && (
          <div className="space-y-1.5">
            <Label>Bertolak Dari</Label>
            <Select value={originId} onValueChange={setOriginId}>
              <SelectTrigger className="h-10" data-testid="origin-location-select">
                <Building2 className="h-4 w-4 mr-2 text-[#10056E]" />
                <SelectValue placeholder="Pilih lokasi bertolak" />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-64">
                {locations.map((l) => (
                  <SelectItem key={l.id} value={l.id}>{l.name}{l.is_hq ? " (Ibu Pejabat)" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {origin?.address && <p className="text-xs text-muted-foreground">{origin.address}</p>}
          </div>
        )}

        <div className="space-y-1.5 relative">
          <Label>Destinasi</Label>
          <div className="relative">
            <MapPin className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="booking-destination-input" required className="pl-9 h-10" placeholder="Taip untuk cari lokasi... cth: KLCC"
              value={form.destination}
              onChange={(e) => { setForm({ ...form, destination: e.target.value }); setDest(null); }} />
          </div>
          {searching && !dest && <p className="text-xs text-muted-foreground">Mencari lokasi...</p>}
          {suggestions.length > 0 && (
            <div className="absolute z-[1000] top-full mt-1 inset-x-0 bg-white border rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid="destination-suggestions">
              {suggestions.map((s, i) => (
                <button key={i} type="button" onClick={() => pickSuggestion(s)} data-testid={`destination-suggestion-${i}`}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 border-b last:border-0">
                  <MapPin className="h-3 w-3 inline mr-1.5 text-[#10056E]" />{s.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {mapPoints.length > 0 && (
          <div className="rounded-lg overflow-hidden border" data-testid="booking-map">
            <MapContainer center={mapPoints[0]} zoom={11} scrollWheelZoom={false} style={{ height: 220, width: "100%", zIndex: 0 }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <FitBounds points={mapPoints} />
              {origin && (
                <CircleMarker center={[origin.lat, origin.lng]} radius={9} pathOptions={{ color: "#fff", weight: 2.5, fillColor: "#10056E", fillOpacity: 1 }} />
              )}
              {dest && (
                <CircleMarker center={[dest.lat, dest.lng]} radius={9} pathOptions={{ color: "#fff", weight: 2.5, fillColor: "#DC3545", fillOpacity: 1 }} />
              )}
              {origin && dest && (
                <Polyline positions={[[origin.lat, origin.lng], [dest.lat, dest.lng]]} pathOptions={{ color: "#10056E", weight: 2.5, dashArray: "6 8" }} />
              )}
            </MapContainer>
          </div>
        )}

        <div className="space-y-1.5">
          <Label>Tujuan Perjalanan</Label>
          <Textarea data-testid="booking-purpose-input" required placeholder="Cth: Mesyuarat penyelarasan di Kementerian..."
            value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Anggaran Jarak (KM)</Label>
            <div className="relative">
              <Route className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="booking-distance-input" type="number" min="1" step="0.1" required className="pl-9 h-10" placeholder="60"
                value={form.estimated_distance} onChange={(e) => setForm({ ...form, estimated_distance: e.target.value })} />
            </div>
            {routeInfo && (
              <p className="text-xs text-[#198754] font-medium" data-testid="route-info">
                Auto-dikira ({routeInfo.method === "pandu" ? "jarak pandu sebenar" : "anggaran"}): {routeInfo.distance_km} km · ~{routeInfo.duration_min} min
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Bilangan Penumpang</Label>
            <div className="relative">
              <Users className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="booking-passengers-input" type="number" min="1" max="15" required className="pl-9 h-10"
                value={form.passengers} onChange={(e) => setForm({ ...form, passengers: e.target.value })} />
            </div>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Arahan Khas Pengguna Kenderaan <span className="text-muted-foreground font-normal">(pilihan)</span></Label>
          <Textarea data-testid="booking-instructions-input" placeholder="Cth: Jemput di lobi utama jam 7.45 pagi; perlu ruang bagasi besar..."
            value={form.special_instructions || ""} onChange={(e) => setForm({ ...form, special_instructions: e.target.value })} />
          <p className="text-xs text-muted-foreground">Arahan ini akan direkodkan dalam Buku Log Kenderaan rasmi.</p>
        </div>

        <p className="text-xs text-muted-foreground bg-slate-50 border rounded-lg p-3">
          Tiada pemilihan kenderaan diperlukan — sistem akan mencadangkan kenderaan paling sesuai kepada pengendali berdasarkan bilangan penumpang, jarak dan ketersediaan.
        </p>

        <Button type="submit" disabled={saving} className="w-full h-11 bg-[#10056E] hover:bg-[#2a17a8]" data-testid="booking-submit-btn">
          <Send className="h-4 w-4 mr-2" /> {saving ? "Menghantar..." : "Hantar Permohonan"}
        </Button>
      </form>
    </div>
  );
}
